// Add-ons of the product page (<product-addons>): the matching kit
// (blocks/product-kit.liquid) and the "Leve junto" cards
// (blocks/product-bundle.liquid). One element per block, one behaviour.
//
// Every add-on is a native checkbox tied to the product form by the form
// attribute and without a name, so it never rides in the form data. It
// carries the variant it adds and that variant's price in cents
// (data-addon-variant, data-addon-price); the size radios beside it
// (data-addon-size, value = variant id, data-price = cents) keep both up
// to date. cart.js collects the checked ones from form.elements and sends
// them with the main piece (docs/sections/cart-drawer.md).
//
// The total inside the buy button is written here too: the variant price
// the section leaves in data-price-cents plus every checked add-on, in the
// store money format that <product-page> keeps in data-money-format. The
// variant swap brings the button back with the variant price alone, so the
// sum is redone on every variant:change.
//
// Conjunto mode (15/10, blocks/product-kit.liquid): an add-on marked
// data-addon-replace is the kit product itself, with two options. The
// block ships a JSON (data-kit-variants) with the kit's variants, which
// option follows the page ("shared") and the page's size per variant.
// Here the page's pick and the chip land on ONE kit variant, which the
// switch carries; checked, it replaces the page's piece (cart.js) and its
// price replaces the variant price in the total. The price beside the
// switch is what the kit adds over the piece alone. No kit variant in
// the page's size: switch disabled and the note shown.
//
// Gift mode (27/10): a zero-priced piece (data-addon-gift) starts on and
// its row prints the struck usual price and "Grátis" in Liquid, so the
// price text is never rewritten here; it adds nothing to the total.
//
// Listeners live on this element and on the document, never on markup
// that product.js replaces.

const ADDON = '[data-addon-variant]';

// Thousands and decimal separator of each Shopify placeholder; the
// no_decimals ones print no decimals at all.
const SEPARATORS = {
  amount: [',', '.'],
  amount_no_decimals: [',', ''],
  amount_with_comma_separator: ['.', ','],
  amount_no_decimals_with_comma_separator: ['.', ''],
  amount_with_apostrophe_separator: ["'", '.'],
  amount_no_decimals_with_space_separator: [' ', ''],
  amount_with_space_separator: [' ', ','],
  amount_with_period_and_space_separator: [' ', '.'],
};

// The format is HTML (entities, sometimes a tag), and the result is
// written as text: decoded once per format.
const formats = new Map();

function moneyFormat(node) {
  const raw = node?.closest('[data-money-format]')?.dataset.moneyFormat;
  if (!raw) return null;
  if (!formats.has(raw)) {
    formats.set(raw, new DOMParser().parseFromString(raw, 'text/html').body.textContent);
  }
  return formats.get(raw);
}

// trimZeros mirrors money_without_trailing_zeros, the filter that prints
// the add-on prices: R$ 149 for 14900, R$ 99,90 for 9990.
function formatMoney(cents, format, trimZeros = false) {
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (placeholder, name) => {
    const [thousands, decimal] = SEPARATORS[name] ?? SEPARATORS.amount;
    const precision = decimal && !(trimZeros && cents % 100 === 0) ? 2 : 0;
    const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  });
}

// Variant price plus every checked add-on of this form, written into the
// price at the top of the page (sections/product.liquid, the [data-price]
// region, which carries the variant price in data-price-cents). Since
// 11/10 the buy button holds no price. With add-ons on, the region is
// marked data-with-addons and its strikethrough and installments step out.
function updateTotal(form) {
  const region = form?.closest('[data-money-format]')?.querySelector('[data-price][data-price-cents]');
  const amount = region?.querySelector('.price__amount');
  const format = moneyFormat(form);
  if (!amount || !format) return;

  let base = Number(region.dataset.priceCents) || 0;
  let extra = 0;
  let replaced = false;
  for (const field of form.elements) {
    if (field.matches(ADDON) && field.checked && !field.disabled) {
      if (field.hasAttribute('data-addon-replace')) {
        base = Number(field.dataset.addonPrice) || base;
        replaced = true;
      } else {
        extra += Number(field.dataset.addonPrice) || 0;
      }
    }
  }
  amount.textContent = formatMoney(base + extra, format);
  region.toggleAttribute('data-with-addons', extra > 0 || replaced);
}

// Rows and chips meet on the size written without spaces, in lower case.
const key = (value) => String(value ?? '').toLowerCase().replace(/\s+/g, '');

class ProductAddons extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.addEventListener('change', this.handleChange, { signal });
    document.addEventListener('variant:change', () => this.refresh(), { signal });

    // Back and forward may restore checked boxes and radios, never the
    // data attributes: the controls are the truth, not the first render.
    this.querySelectorAll('[data-addon-item]').forEach((item) => this.sync(item));
    this.refresh();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  handleChange = (event) => {
    const item = event.target.closest('[data-addon-item]');
    if (!item) return;
    if (event.target.matches('[data-addon-size]')) this.sync(item);
    this.refresh();
  };

  // The chosen size decides the variant and the price the add-on carries.
  // A restored choice that sold out since falls back to the first size
  // still on sale.
  sync(item) {
    const toggle = item.querySelector(ADDON);
    if (!toggle) return;
    if (item.querySelector('[data-kit-variants]')) return this.syncKit(item, toggle);

    const size =
      item.querySelector('[data-addon-size]:checked:enabled') ??
      item.querySelector('[data-addon-size]:enabled');
    if (!size) return;

    size.checked = true;
    toggle.dataset.addonVariant = size.value;
    toggle.dataset.addonPrice = size.dataset.price;

    const price = item.querySelector('[data-addon-price-text]');
    const format = moneyFormat(this);
    if (price && format) {
      price.textContent = formatMoney(Number(size.dataset.price) || 0, format, true);
    }
  }

  // Conjunto mode: the page's size (from the variant the form holds) and
  // the chip pick one kit variant. Every chip is re-read against the
  // page's size, so a size the kit lacks shows struck and disabled.
  syncKit(item, toggle) {
    let data;
    try {
      data = JSON.parse(item.querySelector('[data-kit-variants]').textContent);
    } catch {
      return;
    }
    const form = toggle.form;
    const pageVariant = form?.elements.namedItem('id')?.value;
    const pageSize = key(data.sizes?.[pageVariant]);
    const chips = Array.from(item.querySelectorAll('[data-addon-size]'));

    chips.forEach((chip) => {
      const match = data.variants.find(
        (variant) => key(variant.options[data.shared]) === pageSize && key(variant.options[data.other]) === key(chip.value),
      );
      chip.disabled = !match?.available;
      chip.dataset.variant = match?.id ?? '';
      chip.dataset.price = match?.price ?? '';
    });

    const chip = chips.find((c) => c.checked && !c.disabled) ?? chips.find((c) => !c.disabled);
    const note = item.querySelector('[data-kit-unavailable]');
    if (!chip) {
      toggle.checked = false;
      toggle.disabled = true;
      note?.removeAttribute('hidden');
      return;
    }
    chip.checked = true;
    toggle.disabled = false;
    note?.setAttribute('hidden', '');
    toggle.dataset.addonVariant = chip.dataset.variant;
    toggle.dataset.addonPrice = chip.dataset.price;

    // What the kit adds over the piece alone, never below zero.
    const region = form?.closest('[data-money-format]')?.querySelector('[data-price][data-price-cents]');
    const piece = Number(region?.dataset.priceCents) || 0;
    const price = item.querySelector('[data-addon-price-text]');
    const format = moneyFormat(this);
    if (price && format) {
      price.textContent = formatMoney(Math.max((Number(chip.dataset.price) || 0) - piece, 0), format, true);
    }
  }

  refresh() {
    // The page's size may have changed: the kit pairs again before the sum.
    this.querySelectorAll('[data-addon-item]').forEach((item) => {
      if (item.querySelector('[data-kit-variants]')) this.sync(item);
    });
    updateTotal(this.querySelector(ADDON)?.form);
  }
}

if (!customElements.get('product-addons')) {
  customElements.define('product-addons', ProductAddons);
}
